import React from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import {
  styles,
  NumberAsTwoDigits,
  Row,
  Table,
  TextCell,
  CellTextBoxNoBorder,
} from "./reportingHelpers";
import { parseISO8601 } from "./iso8601";
import { IntlProvider } from "react-intl";
import { FixtureReportsHeader, ScoringTable } from "./FixtureReportsHeader";
import { FixtureReportsFooter } from "./FixtureReportsFooter";

let players = {};

export const PlayByPlayReport = (props) => {
  const { locale, intl } = props;
  const {
    teams,
    fixture,
    scoringIntervals,
    playByPlay,
    roles,
    fixtureProfile: { profile: fixtureProfile },
  } = props.data;
  let homeTeamPeriods,
    awayTeamPeriods = [];
  let periodsScores = [];

  const teamIds = Object.keys(teams);
  const homeTeamId = teams[teamIds[0]].isHome ? teamIds[0] : teamIds[1];
  const awayTeamId = teams[teamIds[0]].isHome ? teamIds[1] : teamIds[0];

  const homeTeam = { ...teams[homeTeamId], entityId: homeTeamId };
  const awayTeam = { ...teams[awayTeamId], entityId: awayTeamId };

  const periodIds = [...new Set(playByPlay.map((play) => play.periodId))];
  const periodLabel = (periodId) => {
    if (periodId < 5) {
      return "Q" + periodId.toString();
    } else {
      return "OT" + (periodId - 10).toString();
    }
  };

  if (homeTeam && awayTeam && scoringIntervals) {
    homeTeamPeriods = parseScoringIntervals(homeTeam, scoringIntervals, intl);
    awayTeamPeriods = parseScoringIntervals(awayTeam, scoringIntervals, intl);

    let lastHomeScore = 0,
      lastAwayScore = 0;
    homeTeamPeriods.forEach((period, index) => {
      const awayPeriod = awayTeamPeriods[index];
      const newHomeScore = period.interval2 || period.interval1;
      const newAwayScore = awayPeriod.interval2 || awayPeriod.interval1;
      periodsScores.push(
        `${newHomeScore - lastHomeScore}-${newAwayScore - lastAwayScore}`
      );
      lastHomeScore = newHomeScore;
      lastAwayScore = newAwayScore;
    });

    homeTeam["score"] = lastHomeScore;
    awayTeam["score"] = lastAwayScore;

    [...homeTeam.players, ...awayTeam.players].forEach((player) => {
      players[player.personId] = player;
    });

    return (
      <IntlProvider locale={locale ?? "en-US"}>
        <Document>
          {periodIds.map((period, index) => {
            const playByPlayRows = playByPlay.filter(
              (event) => event.success && event.periodId === period
            );

            return (
              <Page wrap key={index} size="A4" style={styles.page}>
                <PlayByPlayReportPage
                  periodLabel={periodLabel(period)}
                  {...{
                    homeTeam,
                    awayTeam,
                    playByPlayRows,
                    fixture,
                    homeTeamPeriods,
                    awayTeamPeriods,
                    periodsScores,
                    intl,
                    roles,
                    fixtureProfile,
                  }}
                />
              </Page>
            );
          })}
        </Document>
      </IntlProvider>
    );
  } else {
    return null;
  }
};

const PlayByPlayReportPage = ({
  homeTeam,
  awayTeam,
  playByPlayRows,
  fixture,
  homeTeamPeriods,
  awayTeamPeriods,
  periodsScores,
  periodLabel,
  intl,
  roles,
  fixtureProfile,
}) => {
  const parseClockToString = (event) => {
    const minutes = parseISO8601(event.clock).minutes;
    const seconds = NumberAsTwoDigits({
      value: parseISO8601(event.clock).seconds,
    });
    return `${minutes}:${seconds}`;
  };
  return (
    <React.Fragment>
      <View style={{ marginBottom: "3mm" }}>
        <FixtureReportsHeader
          {...{
            homeTeam,
            awayTeam,
            fixture,
            roles,
            periodsScores,
            title: intl.formatMessage({ id: "reports.playbyplay" }),
            intl,
          }}
        />
      </View>
      <View style={styles.section}>
        <ScoringTable
          {...{
            homeTeam,
            awayTeam,
            homeTeamPeriods,
            awayTeamPeriods,
            intl,
            fixtureProfile,
          }}
        />
      </View>
      <CellTextBoxNoBorder style={{ width: "100%" }}>
        {periodLabel}
      </CellTextBoxNoBorder>
      <Table borderless>
        {playByPlayRows.map((event, index) => {
          const player = players[event.personId];
          const isHomeEvent = homeTeam.entityId === event.entityId;
          const homeTeamScore = event.scores[homeTeam.entityId];
          const awayTeamScore = event.scores[awayTeam.entityId];

          const eventTypeDesc = intl.messages[
            `reports.playbyplay.${event.eventType}`
          ]
            ? intl.formatMessage({
                id: `reports.playbyplay.${event.eventType}`,
              })
            : event.eventType;
          const eventSubTypeDesc = event.subType
            ? intl.messages[`reports.playbyplay.${event.subType}`]
              ? ` ${intl.formatMessage({
                  id: `reports.playbyplay.${event.subType}`,
                })}`
              : ` ${event.subType}`
            : "";
          let description = eventTypeDesc + eventSubTypeDesc;
          if (player) {
            description =
              player.bib +
              " " +
              player.personFullName +
              " " +
              eventTypeDesc +
              eventSubTypeDesc;
          }
          return (
            <React.Fragment key={index}>
              <EventsTableHeader
                intl={intl}
                rowIndex={index}
                homeTeamName={homeTeam.name}
                awayTeamName={awayTeam.name}
              ></EventsTableHeader>
              <Row style={{ fontSize: 7, border: 0, borderLeftWidth: 1 }}>
                <TextCell width="2cm">{parseClockToString(event)}</TextCell>
                <TextCell left flex>
                  {isHomeEvent ? description : " "}
                </TextCell>
                <TextCell width="1.5cm">
                  {homeTeamScore} - {awayTeamScore}
                </TextCell>
                <TextCell width="1cm">{homeTeamScore - awayTeamScore}</TextCell>
                <TextCell left flex>
                  {isHomeEvent ? " " : description}
                </TextCell>
              </Row>
            </React.Fragment>
          );
        })}
      </Table>
      <FixtureReportsFooter roles={roles} intl={intl} />
    </React.Fragment>
  );
};

const EventsTableHeader = ({ rowIndex, homeTeamName, awayTeamName, intl }) => {
  const rowsFittingOnPage = 49;
  const remainder = rowIndex % rowsFittingOnPage;
  if (rowIndex === 0 || remainder === 0) {
    return (
      <Row
        break={rowIndex > 0}
        style={{
          fontSize: 7,
          borderTopWidth: 1,
          borderLeftWidth: 1,
          border: 0,
          fontWeight: "bold",
          backgroundColor: "#f0f0f0",
        }}
      >
        <TextCell width="2cm">
          {intl.formatMessage({ id: "reports.playbyplay.gametime" })}
        </TextCell>
        <TextCell flex>{homeTeamName}</TextCell>
        <TextCell width="1.5cm">
          {intl.formatMessage({ id: "reports.playbyplay.score" })}
        </TextCell>
        <TextCell width="1cm">
          {intl.formatMessage({ id: "reports.playbyplay.diff" })}
        </TextCell>
        <TextCell flex>{awayTeamName}</TextCell>
      </Row>
    );
  } else {
    return null;
  }
};

const parseScoringIntervals = (teamData, scoringIntervals) => {
  const periods = [];

  let newPeriod = {
    interval1: 0,
    interval2: undefined,
    label: "",
    isOverTime: false,
    interval_number_start: 0,
    interval_number_end: 0,
  };
  let periodCount = 1;

  scoringIntervals.forEach((element, index) => {
    if (element.interval_number < 9) {
      if (element.interval_number % 2) {
        newPeriod.interval1 = element[teamData.entityId];
        newPeriod.interval2 = undefined;
        newPeriod.label = "Q" + periodCount.toString();
        newPeriod.isOverTime = false;
        newPeriod.interval_number_start = element.interval_number;
        newPeriod.interval_number_end = element.interval_number;
        periodCount++;

        if (index === scoringIntervals.length - 1) {
          periods.push({ ...newPeriod });
        }
      } else {
        newPeriod.interval2 = element[teamData.entityId];
        newPeriod.interval_number_end = element.interval_number;
        periods.push({ ...newPeriod });
      }
    } else {
      if (element.interval_number === 9) {
        periodCount = 1;
      }
      newPeriod.interval1 = element[teamData.entityId];
      newPeriod.interval_number_start = element.interval_number;
      newPeriod.interval_number_end = element.interval_number;
      newPeriod.label = "OT" + periodCount.toString();
      newPeriod.interval2 = undefined;
      newPeriod.isOverTime = true;
      periodCount++;
      periods.push({ ...newPeriod });
    }
  });

  return periods;
};
